// Start / End date filtering for the client detail page.
//
// Every comparison is on "YYYY-MM-DD" strings, never on Date objects:
// `new Date("2026-09-30")` parses as UTC and lands on the 29th in
// Saskatchewan, so every boundary would be a day early. ISO date strings
// sort lexically in date order, which is all an inclusive range needs.
//
// A blank bound is open-ended on that side; both blank means "all time".

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const dayOf = (v) => (typeof v === 'string' ? v.slice(0, 10) : '');

export function isRangeActive(startDate, endDate) {
  return !!(startDate || endDate);
}

export function rangeIsReversed(startDate, endDate) {
  return !!(startDate && endDate && startDate > endDate);
}

/**
 * Inclusive on both ends. A row with no date can't be placed in a period, so
 * it is excluded once any bound is set -- but kept for all time, which must
 * stay exactly what the page showed before this filter existed.
 */
export function inDateRange(date, startDate, endDate) {
  if (!isRangeActive(startDate, endDate)) return true;
  const d = dayOf(date);
  if (!d) return false;
  if (startDate && d < startDate) return false;
  if (endDate && d > endDate) return false;
  return true;
}

/** All time returns the input array itself, so downstream memos don't churn. */
export function filterByDateRange(rows, startDate, endDate, field = 'date') {
  if (!isRangeActive(startDate, endDate)) return rows;
  return (rows || []).filter((r) => inDateRange(r[field], startDate, endDate));
}

export function addDaysISO(iso, days) {
  const [y, m, d] = iso.split('-').map(Number);
  // Date.UTC in, toISOString out: both UTC, so no local-offset drift.
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function fmtISODate(iso) {
  const [y, m, d] = dayOf(iso).split('-').map(Number);
  if (!y || !m || !d) return iso || '';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
}

export function describeRange(startDate, endDate) {
  if (!isRangeActive(startDate, endDate)) return 'Showing all time';
  if (!startDate) return `Showing up to ${fmtISODate(endDate)}`;
  return `Showing ${fmtISODate(startDate)} – ${endDate ? fmtISODate(endDate) : 'today'}`;
}

/**
 * The client's most recent invoice that has actually gone out: anything but
 * DRAFT (FINAL, SENT, PAID). A row with no status is treated as a draft, the
 * same default utils/rollup.js's arOf() uses. Same-day ties go to the higher
 * id, i.e. the one created last.
 */
export function lastIssuedInvoice(invoices, clientId) {
  let best = null;
  for (const inv of invoices || []) {
    if (Number(inv.client) !== Number(clientId)) continue;
    if ((inv.status || 'DRAFT') === 'DRAFT') continue;
    const d = dayOf(inv.date);
    if (!d) continue;
    if (!best || d > dayOf(best.date) || (d === dayOf(best.date) && inv.id > best.id)) {
      best = inv;
    }
  }
  return best;
}

/** { invoice, startDate: day after it, endDate: '' (through today) } or null. */
export function sinceLastInvoiced(invoices, clientId) {
  const invoice = lastIssuedInvoice(invoices, clientId);
  if (!invoice) return null;
  return { invoice, startDate: addDaysISO(dayOf(invoice.date), 1), endDate: '' };
}
